import {
  EMessageFileAnonymizationStatus,
  type IMessageFileAnonymization,
} from "../types/analytics";
import {
  EMessageFileSource,
  EMessageRole,
  type IMessage,
  type IMessageFileAttachment,
} from "../types/chat";

interface IGetAnonymizableDocumentsProps {
  messages: Pick<IMessage, "role" | "files">[] | null | undefined;
}

interface IIsAnonymizableDocumentProps {
  role: IMessage["role"];
  file: Pick<IMessageFileAttachment, "uuid" | "type" | "source">;
}

/**
 * NOTE: NSAI-6839 — whether the Security Analytics document-anonymization
 * view applies to one attachment: a document a USER uploaded into the chat.
 * Images carry no extracted text, and files the assistant or a tool produced
 * were never anonymized on the way in. A file with no `source` is a legacy
 * upload (the column predates it). Used both for the per-turn list and for the
 * per-card action, so the two can never disagree about a file.
 */
export const isAnonymizableDocument = ({
  role,
  file,
}: IIsAnonymizableDocumentProps): boolean => {
  const isUserUpload =
    file.source === undefined || file.source === EMessageFileSource.USER_UPLOAD;

  return (
    role === EMessageRole.USER &&
    !!file.uuid &&
    isUserUpload &&
    !(file.type ?? "").startsWith("image/")
  );
};

/**
 * NOTE: NSAI-6839 — the anonymizable documents of a set of messages, see
 * `isAnonymizableDocument`. Duplicates — the same file shown on several turns
 * of a window — collapse to one entry, in the order they first appear.
 */
export const getAnonymizableDocuments = ({
  messages,
}: IGetAnonymizableDocumentsProps): IMessageFileAttachment[] => {
  const documents: IMessageFileAttachment[] = [];
  const seenUuids = new Set<string>();

  for (const message of messages ?? []) {
    for (const file of message.files ?? []) {
      if (
        !isAnonymizableDocument({ role: message.role, file }) ||
        seenUuids.has(file.uuid)
      ) {
        continue;
      }

      seenUuids.add(file.uuid);
      documents.push(file);
    }
  }

  return documents;
};

/**
 * NOTE: Literal i18n keys (namespace `analytics`) for each status, spelled out
 * so a typed `t()` accepts the looked-up key without building it dynamically.
 */
export type TDocumentAnonymizationStatusKey =
  | "documentAnonymization.status_anonymized"
  | "documentAnonymization.status_not_masked"
  | "documentAnonymization.status_no_text_content";

const DOCUMENT_ANONYMIZATION_STATUS_KEYS: Record<
  EMessageFileAnonymizationStatus,
  TDocumentAnonymizationStatusKey
> = {
  [EMessageFileAnonymizationStatus.ANONYMIZED]:
    "documentAnonymization.status_anonymized",
  [EMessageFileAnonymizationStatus.NOT_MASKED]:
    "documentAnonymization.status_not_masked",
  [EMessageFileAnonymizationStatus.NO_TEXT_CONTENT]:
    "documentAnonymization.status_no_text_content",
};

/**
 * NOTE: The sentence explaining a status. An unknown value (a server newer than
 * the client) reads as "no text", the one status that claims nothing about
 * what reached the model.
 */
export const getDocumentAnonymizationStatusKey = (
  status: IMessageFileAnonymization["status"],
): TDocumentAnonymizationStatusKey =>
  Object.hasOwn(DOCUMENT_ANONYMIZATION_STATUS_KEYS, status)
    ? DOCUMENT_ANONYMIZATION_STATUS_KEYS[status]
    : "documentAnonymization.status_no_text_content";
